import { useRef, useState } from 'react'
import classNames from 'classnames'

import ArrowDown from 'ui-component-library/icons/arrow-down.svg'

import type { Image, LinkType } from 'types'
import type { Props as BreadcrumbsProps } from '../00-Primitives/Breadcrumbs'
import type { Props as HeroStatsProps } from '../HeroStats'

import AdjustableHeadline from '../00-Primitives/Headlines/AdjustableHeadline'
import ANSIComplianceLogo from '../ANSIComplianceLogo'
import AccmeLogo from '../AccmeLogo'
import GamesLogo from '../GamesLogo'
import PreferredCourseBadge from '../PreferredCourseBadge'

import Breadcrumbs from '../00-Primitives/Breadcrumbs'
import Paragraph from 'content-components/_Primatives/Paragraph'
import Button from 'content-components/_Primatives/Button'
import HeroStats from '../HeroStats'
import { BackgroundImage } from 'content-components/_Primatives/Images'
import YouTube from '../00-Primitives/YouTube'
import Modal from '../00-Primitives/Modal'
import ModalVideoButton from './ModalVideoButton'
import style from './style.scss'

const OVERLAY_BREADCRUMBS = 'breadcrumbs'
const OVERLAY_HERO_STATS = 'hero_stats'
const OVERLAY_NONE = 'none'

type ImageSize = 'tall' | 'medium' | 'short'
type ImageOverlay =
  | typeof OVERLAY_BREADCRUMBS
  | typeof OVERLAY_HERO_STATS
  | typeof OVERLAY_NONE

export type Props = {
  modalVideo?: {
    modalLinkText: string
    videoId: string
  }
  contentAlignment?: 'center'
  contentPosition?: 'bottom' | 'center' | 'middle'
  bodyText?: string
  buttonColor?: 'red' | 'blue'
  ansiVersion?: 'level1' | 'level3'
  breadcrumbs?: BreadcrumbsProps
  className?: string
  displayAnsi?: boolean
  displayAccme?: boolean
  displayGames?: boolean
  displayPreferredCourseBadge?: boolean
  accmeLogoLabelText?: string
  gamesLogoLabelText?: string
  headline: string
  heroStats?: HeroStatsProps
  image?: Image
  imageOverlay?: ImageOverlay
  imageSize?: ImageSize
  preferredCourseBadgeLabelText?: string
  type: 'image' | 'video'
  youtubeVideoId?: string
  links?: { link: LinkType }[]
}

type HeadlineProps = {
  className?: string
  headline: string
  imageSize: ImageSize
}

ContentHeader.defaultProps = {
  breadcrumbs: undefined,
  className: '',
  displayAnsi: false,
  ansiVersion: 'level1',
  displayAccme: false,
  displayGames: false,
  displayPreferredCourseBadge: false,
  headline: '',
  heroStats: undefined,
  imageSize: 'tall',
  preferredCourseBadgeLabelText: '',
  youtubeVideoId: '',
}

const renderHeadline = (props: HeadlineProps) => {
  const { className, headline, imageSize } = props
  const breakpoints = {
    tall: undefined,
    medium: { two: 26 },
    short: { two: 20 },
  }

  return (
    <AdjustableHeadline
      characterBreakpoints={breakpoints[imageSize]}
      className={className}
    >
      {headline}
    </AdjustableHeadline>
  )
}

function ContentHeader(props: Props) {
  const heroContainer = useRef<HTMLDivElement | null>(null)
  const [isShowingModalVideo, setIsShowingModalVideo] = useState(false)

  const scrollDown = () => {
    if (heroContainer.current) {
      const bottom = heroContainer.current.getBoundingClientRect().bottom

      if (bottom) {
        window.scroll({
          top: bottom,
          left: 0,
          behavior: 'smooth',
        })
      }
    }
  }

  const {
    buttonColor,
    breadcrumbs,
    className,
    displayAnsi,
    ansiVersion,
    displayAccme,
    displayGames,
    displayPreferredCourseBadge,
    accmeLogoLabelText,
    gamesLogoLabelText,
    headline,
    heroStats,
    image,
    imageOverlay,
    imageSize = 'tall',
    preferredCourseBadgeLabelText,
    type,
    youtubeVideoId = '',
    bodyText,
    contentPosition,
    contentAlignment,
    links,
    modalVideo,
  } = props
  const headlineProps = {
    className: style.headline,
    headline,
    imageSize,
  }

  function handleModalVideoToggle() {
    setIsShowingModalVideo((s) => !s)
  }

  // return null

  if (type === 'video') {
    return (
      <YouTube
        autoplay
        className={classNames(style.heroVideo, style.tall, className)}
        videoId={youtubeVideoId}
      />
    )
  }

  if (image) {
    return (
      <div ref={heroContainer}>
        <BackgroundImage
          className={classNames(
            style.wrapper,
            imageSize && style[imageSize],
            className,
            style.image,
          )}
          image={image}
        >
          <div
            className={classNames(
              style.text,
              contentPosition && style[contentPosition],
              {
                [style.center]: contentAlignment === 'center',
              },
            )}
          >
            {renderHeadline(headlineProps)}
            {bodyText && (
              <Paragraph className={style.bodyText}>{bodyText}</Paragraph>
            )}

            {links && (
              <div className={style.links}>
                {links.map(({ link }) => (
                  <Button
                    key={link.url}
                    className={style.link}
                    url={link.url}
                    variant={buttonColor === 'red' ? 'primary' : 'primaryBlue'}
                  >
                    {link.text}
                  </Button>
                ))}
              </div>
            )}

            {modalVideo && (
              <>
                <ModalVideoButton onClick={handleModalVideoToggle}>
                  {modalVideo.modalLinkText}
                </ModalVideoButton>
                {isShowingModalVideo && (
                  <Modal>
                    <div
                      className={style.modal}
                      onClick={handleModalVideoToggle}
                    >
                      <i className={style.closeIcon} />
                      <div className={style.videoWrapper}>
                        <YouTube
                          autoplay
                          className={style.video}
                          videoId={modalVideo.videoId}
                        />
                      </div>
                    </div>
                  </Modal>
                )}
              </>
            )}
          </div>
          {heroStats && imageOverlay === OVERLAY_HERO_STATS && (
            <HeroStats className={style.heroStats} {...heroStats} />
          )}
          {breadcrumbs && imageOverlay === OVERLAY_BREADCRUMBS && (
            <Breadcrumbs className={style.heroStats} {...breadcrumbs} />
          )}
          {displayAnsi && (
            <ANSIComplianceLogo
              ansiVersion={ansiVersion}
              className={style.ansiLogo}
            />
          )}
          {displayGames && (
            <GamesLogo
              ariaLabel={gamesLogoLabelText}
              className={style.gamesLogo}
            />
          )}
          {displayAccme && (
            <AccmeLogo
              ariaLabel={accmeLogoLabelText}
              className={style.accmeLogo}
            />
          )}
          {displayPreferredCourseBadge && (
            <PreferredCourseBadge
              className={style.badge}
              preferredCourseBadgeLabelText={preferredCourseBadgeLabelText}
            />
          )}
          {imageSize === 'tall' && (
            <button
              aria-hidden
              className={style.arrowDown}
              tabIndex={-1}
              type='button'
              onClick={scrollDown}
            >
              <ArrowDown />
            </button>
          )}
        </BackgroundImage>
      </div>
    )
  }

  return null
}

export default ContentHeader
